import { Activity, ActivityStates } from '@/types/activities'
import { Answer, AnswerResults, Question, QuestionId, QuestionsFormData, DepartmentType } from '@/types/questions'
import { ReactElement, ReactNode, createContext, use, useCallback, useEffect, useMemo, useState } from 'react'
import { Control, FieldValues, useForm } from 'react-hook-form'
import { RegistrationCheckContext } from '@/providers/registration-check-provider'
import {
    areVisibleQuestionsAnswered,
    getVisibleQuestionIds,
} from '@/components/registration-check/_helpers/getVisibleQuestionIds'

type Props = {
    activity: Activity
    canBeDeleted: boolean
    deleteActivity: () => void
    updateActivity: (activity: Activity) => void
    children: ReactNode
}

type ActivityContextValue = {
    activity: Activity
    canBeDeleted: boolean
    deleteActivity: () => void
    control: Control<FieldValues, unknown>
    formData: QuestionsFormData
    questions: Question[]
    visibleQuestions: QuestionId[]
    updateActivityFromForm: (formData?: QuestionsFormData) => void
    getQuestionIdFromFormDataKey: (formDataKey: string) => string
}

export const ActivityContext = createContext<ActivityContextValue | undefined>(undefined)

/**
 * Provider that encapsulates the state and logic for a single Activity flow.
 * Presents the necessary data and handlers to children through context.
 */
export const ActivityProvider = ({
    activity,
    canBeDeleted,
    deleteActivity,
    updateActivity,
    children,
}: Props): ReactElement => {
    const { questions } = use(RegistrationCheckContext)
    const { control, watch, getValues } = useForm()
    const formData = watch() as QuestionsFormData

    const [visibleQuestions, setVisibleQuestions] = useState<QuestionId[]>(
        questions && questions.length > 0 ? [questions[0].id] : [],
    )

    /** Gets the "questionId" from the "formDataKey"
     * @param {string} formDataKey String of format "ACactivityId_questionId"
     */
    const getQuestionIdFromFormDataKey = (formDataKey: string): string => formDataKey.split('_')[1]

    const setNewVisibleQuestions = useCallback((): void => {
        setVisibleQuestions(getVisibleQuestionIds(questions ?? [], activity, getValues() as QuestionsFormData))
    }, [activity, getValues, questions])

    const updateActivityFromForm = useCallback(
        (_?: QuestionsFormData): void => {
            const currentValues = getValues() as QuestionsFormData
            const newActivity: Activity = { ...activity }

            // Set new empty arrays of RegistrationKeys and AnswerResults
            const keys: string[] = []
            const nonContractKeys: string[] = []
            const departmentTypes: DepartmentType[] = []
            const results: AnswerResults[] = []

            // Get formEntries as iterable array from formData
            const formEntries: [string, string | string[] | null | undefined][] = Object.entries(currentValues)
            if (formEntries.length > 0) {
                // Go through each formEntry to check if a RegistrationKey or AnswerResult should be registered
                formEntries.forEach(([formDataKey, givenAnswers]) => {
                    const questionId = getQuestionIdFromFormDataKey(formDataKey)
                    const question = questions.find((question: Question) => question.id === questionId)
                    const answers: Answer[] =
                        question?.questionContent.answers.filter((answer: Answer) => {
                            // Because of checkbox-questions and multiselect-questions, a givenAnswer CAN be an array of answerIds
                            if (Array.isArray(givenAnswers)) {
                                return givenAnswers.includes(answer.id)
                            } else {
                                return answer.id === givenAnswers
                            }
                        }) ?? []

                    // For each given answer we have to check/push the registrationKey and result
                    answers.forEach((answer) => {
                        const key = answer?.result?.registrationKey
                        const nonContractRegistrationKey = answer?.result?.nonContractRegistrationKey
                        const departmentType = answer?.result?.departmentType

                        if (key) {
                            keys.push(key)
                        }
                        if (nonContractRegistrationKey) {
                            nonContractKeys.push(nonContractRegistrationKey)
                        }
                        if (departmentType) {
                            departmentTypes.push(departmentType)
                        }
                        results.push(answer?.result?.type)
                    })
                })

                // Remove duplicates from non-contract keys and registration departments
                const uniqueNonContractKeys = [...new Set(nonContractKeys)]
                const uniqueDepartmentTypes = [...new Set(departmentTypes)]

                // Update activity with new registrationKeys, answerResults and departmentTypes
                newActivity.answerResults = results
                newActivity.registrationKeys = keys
                newActivity.nonContractRegistrationKeys = uniqueNonContractKeys
                newActivity.departmentTypes = uniqueDepartmentTypes
            }

            newActivity.status = areVisibleQuestionsAnswered(questions ?? [], activity, currentValues)
                ? ActivityStates.FINISHED
                : ActivityStates.IN_PROGRESS

            updateActivity(newActivity)
        },
        [activity, getValues, questions, updateActivity],
    )

    useEffect(() => {
        const subscription = watch(() => {
            setNewVisibleQuestions()
            updateActivityFromForm()
        })
        return () => subscription.unsubscribe()
    }, [watch, setNewVisibleQuestions, updateActivityFromForm])

    const value = useMemo<ActivityContextValue>(
        () => ({
            activity,
            canBeDeleted,
            deleteActivity,
            control,
            formData,
            questions,
            visibleQuestions,
            updateActivityFromForm,
            getQuestionIdFromFormDataKey,
        }),
        [
            activity,
            canBeDeleted,
            deleteActivity,
            control,
            formData,
            questions,
            visibleQuestions,
            updateActivityFromForm,
            getQuestionIdFromFormDataKey,
        ],
    )

    return <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>
}
